import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { trackSignUp } from "../utils/analytics";
import { newEventId } from "../utils/eventId";

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

axios.defaults.withCredentials = true;

// Attach Bearer token to every outgoing request automatically.
// Safari's ITP blocks cross-origin cookies (e.g. localhost:3000 → localhost:5000
// on dev, or frontend domain → API domain in production), causing 401 errors
// and unexpected logouts.  The localStorage token is immune to ITP restrictions.
axios.interceptors.request.use(
  (config) => {
    // Skip for direct S3 uploads — presigned URLs already contain auth
    // and adding a Bearer token causes S3 to reject the request.
    const url = config.url || "";
    if (url.includes("amazonaws.com") || url.includes("s3.")) {
      return config;
    }

    const token = localStorage.getItem("token");
    if (token) {
      config.headers = config.headers || {};
      // Only set if not already explicitly set by the caller
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Set up axios interceptor for handling 401 errors globally
// Only force-logout on 401 from core auth endpoints — not data-fetch routes
// whose 401 can be a harmless race condition (e.g. /ai/usage fires before token is set).
const AUTH_LOGOUT_PATHS = ["/auth/me", "/auth/verify", "/auth/login"];
const AUTH_BYPASS_PATHS = ["/login", "/signup"];

const redirectToLogin = () => {
  const currentPath = window.location.pathname;
  if (!AUTH_BYPASS_PATHS.includes(currentPath)) {
    window.location.replace("/login");
  }
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || "";
      const isAuthEndpoint = AUTH_LOGOUT_PATHS.some((p) => url.includes(p));

      if (isAuthEndpoint) {
        // Clear token and user data from localStorage
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Set logout flag for other tabs
        localStorage.setItem("logout-flag", Date.now().toString());

        // Redirect to login from any authenticated area
        redirectToLogin();
      }
    }
    return Promise.reject(error);
  },
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const useUser = () => {
  const { user } = useAuth();
  return user;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(user);

  // Keep userRef in sync with user state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const fetchFullUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const config = token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};
      const response = await axios.get(`${API_URL}/auth/me`, config);
      const fetchedUser = response.data?.user || response.data;

      if (fetchedUser && typeof fetchedUser === "object") {
        setUser((prevUser) => {
          const fullUser = { ...(prevUser || {}), ...fetchedUser };
          localStorage.setItem("user", JSON.stringify(fullUser));
          return fullUser;
        });
      }
    } catch (error) {
      console.error("Failed to fetch full user data from /api/auth/me:", error);
    }
  }, []);

  const handleLogoutFromOtherTab = useCallback(() => {
    // Clear user state
    setUser(null);
    // Clear token if still exists
    localStorage.removeItem("token");
    // Redirect to login from any authenticated area
    redirectToLogin();
  }, []);

  useEffect(() => {
    // On mount, check localStorage for token and user data (no API call)
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (token && storedUser) {
      try {
        // Restore user from localStorage
        const userData = JSON.parse(storedUser);
        setUser(userData);

        // Fetch full user data and merge
        fetchFullUser();
      } catch (error) {
        // If parsing fails, clear everything
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
      }
    } else {
      // No token or user data, clear state
      setUser(null);
    }

    // Set loading to false immediately (no async operation)
    setLoading(false);

    // Listen for storage changes (cross-tab login/logout/update detection)
    const handleStorageChange = (e) => {
      try {
        // 1. DETECT LOGOUT
        if ((e.key === "token" && e.newValue === null) || (e.key === "logout-flag" && e.newValue)) {
          if (userRef.current) {
            handleLogoutFromOtherTab();
          }
          if (e.key === "logout-flag") {
            // Consuming the flag
            setTimeout(() => localStorage.removeItem("logout-flag"), 1000);
          }
          return;
        }

        // 2. DETECT LOGIN
        if (e.key === "token" && e.newValue !== null) {
          if (!userRef.current) {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
              try {
                const userData = JSON.parse(storedUser);
                setUser(userData);
                fetchFullUser();
              } catch (parseError) {
                console.warn("Failed to parse user data during login sync:", parseError);
              }
            }
          }
          return;
        }

        // 3. DETECT PROFILE UPDATES
        if (e.key === "user" && e.newValue) {
          try {
            const updatedUser = JSON.parse(e.newValue);
            // Only update if we're logged in and data is actually different
            if (userRef.current && JSON.stringify(updatedUser) !== JSON.stringify(userRef.current)) {
              setUser(updatedUser);
            }
          } catch (parseError) {
            console.warn("Failed to parse user data during profile sync:", parseError);
          }
        }
      } catch (error) {
        // Safari may block localStorage access during navigation
        console.warn("Storage event handler error (Safari ITP?):", error);
      }
    };

    // Listen for storage events (fired when localStorage changes in other tabs)
    window.addEventListener("storage", handleStorageChange);

    // Also check periodically for logout flag (in case storage event doesn't fire)
    // Use a counter to prevent false positives from temporary localStorage access issues in Safari
    let missingTokenCount = 0;
    const REQUIRED_MISSING_COUNT = 3; // Require 3 consecutive checks before logging out

    const checkLogoutFlag = setInterval(() => {
      try {
        // iOS/Android often throttle or block storage while the tab is backgrounded;
        // skip token checks until the user returns to avoid false "missing token" streaks.
        if (
          typeof document !== "undefined" &&
          document.visibilityState !== "visible"
        ) {
          return;
        }

        const logoutFlag = localStorage.getItem("logout-flag");
        const token = localStorage.getItem("token");

        // 1. FALLBACK FOR LOGOUT
        if (logoutFlag && userRef.current) {
          handleLogoutFromOtherTab();
          localStorage.removeItem("logout-flag");
          missingTokenCount = 0;
          return;
        }

        // Detect missing token
        if (!token && userRef.current) {
          missingTokenCount++;
          if (missingTokenCount >= REQUIRED_MISSING_COUNT) {
            handleLogoutFromOtherTab();
            missingTokenCount = 0;
          }
        } else {
          missingTokenCount = 0;

          // 2. FALLBACK FOR LOGIN SYNC
          if (token && !userRef.current) {
            const storedUser = localStorage.getItem("user");
            if (storedUser) {
              try {
                setUser(JSON.parse(storedUser));
                fetchFullUser();
              } catch (e) { }
            }
          }

          // 3. FALLBACK FOR PROFILE SYNC
          if (token && userRef.current) {
            const storedUserString = localStorage.getItem("user");
            if (storedUserString) {
              try {
                const storedUser = JSON.parse(storedUserString);
                if (JSON.stringify(storedUser) !== JSON.stringify(userRef.current)) {
                  setUser(storedUser);
                }
              } catch (e) { }
            }
          }
        }
      } catch (error) {
        console.warn("localStorage access error (Safari ITP?):", error);
        missingTokenCount = 0;
      }
    }, 1000); // Check every second

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(checkLogoutFlag);
    };
  }, [handleLogoutFromOtherTab, fetchFullUser]);

  const verifyToken = async () => {
    try {
      // Try with cookies first (preferred method)
      let response;
      try {
        response = await axios.get(`${API_URL}/auth/verify`);
      } catch (cookieError) {
        // If cookie-based auth fails, try with localStorage token (Safari fallback)
        const token = localStorage.getItem("token");
        if (token) {
          response = await axios.get(`${API_URL}/auth/verify`, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          throw cookieError;
        }
      }

      if (response && response.data.user) {
        setUser(response.data.user);
        // Update localStorage with token and user data
        if (response.data.token) {
          localStorage.setItem("token", response.data.token);
        }
        if (response.data.user) {
          localStorage.setItem("user", JSON.stringify(response.data.user));
        }
        return response.data.user;
      }
      return null;
    } catch (error) {
      // Clear localStorage if verification fails
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, area) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
        // Which side of the product the visitor picked in the login dropdown.
        // Only affects the error copy the server returns; the credentials and
        // the session are the same either way.
        ...(area ? { area } : {}),
      });
      const { user: userData, token } = response.data;
      setUser(userData);
      // Store token and user data in localStorage
      if (token) {
        localStorage.setItem("token", token);
      }
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
      // Clear any stale logout-flag from a previous failed token verification
      // (e.g. mount-time fetchFullUser with an expired token sets this flag,
      // and the periodic checker would otherwise pick it up and log us out)
      localStorage.removeItem("logout-flag");
      await fetchFullUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Login failed",
        code: error.response?.data?.code || "",
      };
    }
  };

  const requestSignup = async (name, email, password, role = "Other") => {
    try {
      const response = await axios.post(`${API_URL}/auth/request-signup`, {
        name,
        email,
        password,
        role,
      });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message:
          error.response?.data?.message || "Failed to send verification email",
      };
    }
  };

  const signup = async (
    name,
    email,
    password,
    token = null,
    testerToken = null,
    userToken = null,
    role = "Other",
  ) => {
    try {
      const event_id = newEventId();
      const payload = { name, email, password, role, event_id };
      if (token) {
        payload.token = token;
      }
      if (testerToken) {
        payload.testerToken = testerToken;
      }
      if (userToken) {
        payload.userToken = userToken;
      }
      const response = await axios.post(`${API_URL}/auth/signup`, payload);
      const { user: userData, token: authToken } = response.data;
      setUser(userData);
      if (authToken) {
        localStorage.setItem("token", authToken);
      }
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
      localStorage.removeItem("logout-flag");
      // Browser-side sign_up — server fires the same event_id for dedup.
      trackSignUp({
        eventId: event_id,
        user: userData
          ? { email: userData.email, userId: userData.id || userData._id }
          : { email },
        method: "email",
      }).catch(() => {});
      await fetchFullUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Signup failed",
      };
    }
  };

  const invitedSignup = async (
    name,
    email,
    password,
    testerToken = null,
    userToken = null,
    role = "Other",
  ) => {
    try {
      const event_id = newEventId();
      const validRoles = ["Attorney", "Paralegal", "Legal Researcher", "Other"];
      const payload = {
        name,
        email,
        password,
        role: validRoles.includes(role) ? role : "Other",
        event_id,
      };
      if (testerToken) {
        payload.testerToken = testerToken;
      }
      if (userToken) {
        payload.userToken = userToken;
      }
      const response = await axios.post(
        `${API_URL}/auth/invited-signup`,
        payload,
      );
      const { user: userData, token: authToken } = response.data;
      setUser(userData);
      if (authToken) {
        localStorage.setItem("token", authToken);
      }
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
      localStorage.removeItem("logout-flag");
      trackSignUp({
        eventId: event_id,
        user: userData
          ? { email: userData.email, userId: userData.id || userData._id }
          : { email },
        method: "email",
      }).catch(() => {});
      await fetchFullUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Signup failed",
      };
    }
  };

  const signInWithGoogle = async (credential, mode = "signin", role = null) => {
    try {
      const event_id = newEventId();
      const payload = { credential, mode, event_id };
      if (role) payload.role = role;
      const response = await axios.post(`${API_URL}/auth/google`, payload);
      const { user: userData, token } = response.data;
      setUser(userData);
      // Store token and user data in localStorage
      if (token) {
        localStorage.setItem("token", token);
      }
      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
      }
      localStorage.removeItem("logout-flag");
      if (mode === "signup") {
        trackSignUp({
          eventId: event_id,
          user: userData
            ? { email: userData.email, userId: userData.id || userData._id }
            : undefined,
          method: "google",
        }).catch(() => {});
      }
      await fetchFullUser();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Google sign-in failed",
      };
    }
  };

  const logout = async () => {
    // Clear user state and localStorage token/user immediately
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");

    // Set logout flag to notify other tabs
    localStorage.setItem("logout-flag", Date.now().toString());

    // Remove the flag after a short delay to allow other tabs to detect it
    setTimeout(() => {
      localStorage.removeItem("logout-flag");
    }, 100);

    // Attempt to call logout endpoint, but silently ignore any errors
    // (401 is expected if session is already invalid)
    try {
      await axios.post(
        `${API_URL}/auth/logout`,
        {},
        {
          validateStatus: (status) => status < 500, // Don't throw on 401/403, only on server errors
        },
      );
    } catch (error) {
      // Silently ignore all errors during logout
      // This prevents 401 errors from being logged or shown to the user
      if (error.response?.status !== 401) {
        // Only log non-401 errors (unexpected errors)
        console.warn("Logout request failed:", error.message);
      }
    }

    redirectToLogin();
  };

  const updateProfile = async (payload) => {
    try {
      const response = await axios.put(`${API_URL}/auth/profile`, payload);
      const updatedUser = response.data.user;
      setUser(updatedUser);
      // Update user data in localStorage
      if (updatedUser) {
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
      return {
        success: true,
        user: updatedUser,
        message: response.data.message,
        emailVerificationSent: response.data.emailVerificationSent || false,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Unable to update profile",
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    invitedSignup,
    requestSignup,
    signInWithGoogle,
    logout,
    isAuthenticated: !!user,
    isAdmin:
      user?.isAdmin === true ||
      user?.is_Admin === true ||
      user?.is_Admin === "yes" ||
      user?.is_Admin === "Yes",
    updateProfile,
    verifyToken,
    fetchFullUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
